import { Dropdown, Option } from '@fluentui/react-components';
import { PeopleRegular } from '@fluentui/react-icons';
import {
  ClassbookEntryDefinition,
  ClassbookEntryDefinitionCategory,
  UserListSorting,
  getSortedUserLabel,
  sortUsersByPreference,
} from '@mnspro-cloud/services';
import { Person } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { isEqual } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../Button';
import { Loader } from '../../../Loader/Loader';
import { ResponsiveDialog } from '../../../ResponsiveDialog/ResponsiveDialog';
import {
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  TagPickerTag,
} from '../../../TagPicker';
import { Textarea } from '../../../Textarea';
import './ClassbookEntryDialog.scss';

export interface ClassbookEntryDialogProps {
  open: boolean;
  onCancelClick: () => void;
  onSaveClick(draft: ClassbookEntryDraft): void;
  classbookEntryCategories: ClassbookEntryDefinitionCategory[];
  classbookEntryDefinitions: ClassbookEntryDefinition[];
  students: Person[];
  isLoading: boolean;
  isUpdating?: boolean;
  entry?: ClassbookEntryDraft;
  preferenceSorting?: UserListSorting;
}

export interface ClassbookEntryDraft {
  id?: string;
  affectedUsers: Person[];
  definitionId?: string;
  categoryId?: string;
  occured: string;
  comment: string;
  groupId?: string;
}

/**
 * @translate
 */
export const ClassbookEntryDialog: React.FC<ClassbookEntryDialogProps> = (props) => {
  const { t } = useTranslation();
  const {
    students,
    classbookEntryCategories,
    classbookEntryDefinitions,
    onCancelClick,
    onSaveClick,
    open,
    entry,
    isLoading,
    isUpdating,
    preferenceSorting,
  } = props;

  const [entryDraft, setEntryDraft] = useState(entry);
  const updateEntry = (value: Partial<ClassbookEntryDraft>) => {
    if (!entryDraft) {
      setEntryDraft({
        id: value.id ?? '',
        affectedUsers: value.affectedUsers ?? [],
        definitionId: value.definitionId,
        categoryId: value.categoryId,
        occured: value.occured ?? moment().toISOString(),
        comment: value.comment ?? '',
        groupId: value.groupId,
      });
    } else {
      setEntryDraft({ ...entryDraft, ...value });
    }
  };

  const isValid = (): boolean => !!entryDraft?.definitionId && !!entryDraft?.categoryId;

  const filteredDefinitions = classbookEntryDefinitions.filter(
    (d) => d.category?.id === entryDraft?.categoryId,
  );

  const selectedCategory = classbookEntryCategories.find((c) => c.id === entryDraft?.categoryId);
  const selectedDefinition = filteredDefinitions.find((d) => d.id === entryDraft?.definitionId);

  useEffect(() => {
    setEntryDraft(entry);
  }, [entry]);

  const sortMode = preferenceSorting ?? UserListSorting.FirstName;
  const sortedStudents = sortUsersByPreference(students, sortMode);

  const selectedStudentIds = (entryDraft?.affectedUsers ?? []).map((u) => u.id);
  const studentById = new Map(students.map((s) => [s.id, s]));
  const studentLabel = (studentId: string) => {
    const s = studentById.get(studentId);
    return s ? getSortedUserLabel(s, sortMode) : studentId;
  };

  return (
    <ResponsiveDialog
      data-testid="ClassRegisterEntry"
      open={open}
      className="classbookEntryDialog"
      header={
        entry
          ? t('classbook:classbookentry_edit', { defaultValue: 'Klassenbucheintrag bearbeiten' })
          : t('classbook:classbookentry_new', { defaultValue: 'Neuer Klassenbucheintrag' })
      }
      primaryAction={
        <Button
          appearance="primary"
          onClick={() => {
            if (!entryDraft) {
              return;
            }
            onSaveClick(entryDraft);
          }}
          disabled={!isValid() || isUpdating}
          loading={isUpdating}
        >
          {t('save', { defaultValue: 'Speichern' })}
        </Button>
      }
      secondaryAction={
        <Button
          aria-label={t('classbook:classbookentry_cancel_label', {
            defaultValue: 'Schließt den Dialog',
          })}
          disabled={isUpdating}
          onClick={onCancelClick}
        >
          {t('cancel', { defaultValue: 'Abbrechen' })}
        </Button>
      }
    >
      {isLoading ? (
        <Loader />
      ) : (
        <div className="flex flex-col gap-2 contentWrapper">
          <span id="category-id">
            {t('classbook:classbookentry_category', { defaultValue: 'Eintragskategorie*' })}
          </span>
          <Dropdown
            appearance="filled-darker"
            disabled={isUpdating}
            aria-required={true}
            aria-labelledby="category-id"
            placeholder={t('classbook:classbookentry:category_choose', {
              defaultValue: 'Eintragskategorie auswählen...',
            })}
            value={selectedCategory?.shortName ?? ''}
            selectedOptions={selectedCategory?.id ? [selectedCategory.id] : []}
            onOptionSelect={(_, data) => {
              updateEntry({
                categoryId: data.optionValue,
                definitionId: '',
              });
            }}
          >
            {classbookEntryCategories.map((category) => (
              <Option key={category.id} value={category.id} text={category.shortName}>
                {category.shortName}
              </Option>
            ))}
          </Dropdown>

          <span id="definition-id">
            {t('classbook:classbookentry:entrytype', { defaultValue: 'Eintragstyp*' })}
          </span>
          <Dropdown
            appearance="filled-darker"
            aria-required={true}
            aria-labelledby="definition-id"
            placeholder={t('classbook:classbookentry:entrytype_choose', {
              defaultValue: 'Eintragstyp auswählen...',
            })}
            disabled={!entryDraft?.categoryId || isUpdating}
            value={selectedDefinition?.shortName ?? ''}
            selectedOptions={selectedDefinition?.id ? [selectedDefinition.id] : []}
            onOptionSelect={(_, data) => {
              updateEntry({
                definitionId: data.optionValue,
              });
            }}
          >
            {filteredDefinitions.map((definition) => (
              <Option key={definition.id} value={definition.id} text={definition.shortName}>
                {definition.shortName}
              </Option>
            ))}
          </Dropdown>

          <span>{t('Student', { defaultValue: 'Schüler' })}</span>
          <div className="flex gap-2">
            {!isEqual(entryDraft?.affectedUsers, students) && (
              <Button
                disabled={isUpdating}
                icon={<PeopleRegular />}
                size="small"
                appearance="outline"
                onClick={() =>
                  updateEntry({
                    affectedUsers: [...students],
                  })
                }
              >
                {t('classbook:classbookentry:select_all_students', {
                  defaultValue: 'Alle Schüler auswählen',
                })}
              </Button>
            )}

            {entryDraft?.affectedUsers && entryDraft?.affectedUsers.length > 0 && (
              <Button
                disabled={isUpdating}
                icon={<PeopleRegular />}
                size="small"
                appearance="outline"
                onClick={() =>
                  updateEntry({
                    affectedUsers: [],
                  })
                }
              >
                {t('classbook:classbookentry:deselect_all_students', {
                  defaultValue: 'Alle Schüler abwählen',
                })}
              </Button>
            )}
          </div>

          <TagPicker
            appearance="filled-darker"
            disabled={isUpdating}
            positioning={{ autoSize: 'height', overflowBoundaryPadding: 16 }}
            selectedOptions={selectedStudentIds}
            onOptionSelect={(_, data) => {
              if (isUpdating) {
                return;
              }
              const selectedIds = data.selectedOptions;
              updateEntry({
                affectedUsers: students.filter((s) => selectedIds.includes(s.id)),
              });
            }}
          >
            <TagPickerControl className="studentDropdown">
              <TagPickerGroup>
                {selectedStudentIds.map((id) => (
                  <TagPickerTag
                    key={id}
                    shape="rounded"
                    value={id}
                    dismissible
                    onClick={() => {
                      if (isUpdating || !entryDraft?.affectedUsers) {
                        return;
                      }
                      updateEntry({
                        affectedUsers: entryDraft.affectedUsers.filter((u) => u.id !== id),
                      });
                    }}
                  >
                    {studentLabel(id)}
                  </TagPickerTag>
                ))}
              </TagPickerGroup>
              <TagPickerInput
                placeholder={
                  selectedStudentIds.length === 0
                    ? t('classbook:classbookentry:select_students', {
                        defaultValue: 'Schüler auswählen...',
                      })
                    : ''
                }
                onRemoveLast={() => {
                  if (isUpdating || !entryDraft?.affectedUsers?.length) {
                    return;
                  }
                  updateEntry({
                    affectedUsers: entryDraft.affectedUsers.slice(0, -1),
                  });
                }}
              />
            </TagPickerControl>
            <TagPickerList>
              {sortedStudents
                .filter((s) => !selectedStudentIds.includes(s.id))
                .map((student) => {
                  const label = getSortedUserLabel(student, sortMode);
                  return (
                    <TagPickerOption key={student.id} value={student.id}>
                      {label}
                    </TagPickerOption>
                  );
                })}
            </TagPickerList>
          </TagPicker>

          <span>{t('classbook:classbookentry:note', { defaultValue: 'Notiz' })}</span>
          <Textarea
            appearance="filled-darker"
            disabled={isUpdating}
            value={entryDraft?.comment ?? ''}
            resize="vertical"
            placeholder={t('classbook:classbookentry:note_entry', {
              defaultValue: 'Notiz zu dem Klassenbucheintrag...',
            })}
            onChange={(_, data) => {
              updateEntry({
                comment: data?.value,
              });
            }}
          />
        </div>
      )}
    </ResponsiveDialog>
  );
};
